import { useMemo } from 'react';
import { useTypedQuery } from 'apiTyped/helpers/useTypedQuery';
import type {
  AlertItem,
  Severity,
  AlertStatus,
  FilterType
} from 'v2/pages/Alerts.types';

export interface AlertsFilters {
  severity?: Severity | null;
  status?: AlertStatus | null;
  types?: FilterType[];
  search?: string | null;
  market?: string | null;
  page?: number;
  limit?: number;
}

export interface AlertsResponse {
  alerts: AlertItem[];
  sevCounts: Record<Severity, number>;
  sevNew: Record<Severity, number>;
  filterTypes: FilterType[];
  total: number;
}

export const useAlerts = (filters: AlertsFilters = {}) => {
  const { isLoading, data, refetch } = useTypedQuery('get', '/v2/alerts/', {
    params: {
      query: {
        _severity: filters.severity ?? undefined,
        _status: filters.status ?? undefined,
        _types: filters.types?.join(',') || undefined,
        _search: filters.search ?? undefined,
        _market: filters.market ?? undefined,
        _page: filters.page ?? undefined,
        _limit: filters.limit ?? undefined
      }
    }
  });

  const mapped = useMemo((): AlertsResponse | null => {
    if (!data) return null;
    return {
      alerts: (data as any).alerts ?? [],
      sevCounts: (data as any).sev_counts ?? {
        crit: 0,
        warn: 0,
        info: 0,
        good: 0
      },
      sevNew: (data as any).sev_new ?? { crit: 0, warn: 0, info: 0, good: 0 },
      filterTypes: (data as any).filter_types ?? [],
      total: (data as any).total ?? 0
    };
  }, [data]);

  return {
    isLoading,
    alerts: mapped?.alerts ?? [],
    sevCounts: mapped?.sevCounts ?? { crit: 0, warn: 0, info: 0, good: 0 },
    sevNew: mapped?.sevNew ?? { crit: 0, warn: 0, info: 0, good: 0 },
    filterTypes: mapped?.filterTypes ?? [],
    total: mapped?.total ?? 0,
    refetch
  };
};
